
import { EventBus } from "@/plugins/eventBus"
import axios, { AxiosError, AxiosResponse } from "axios"
import Vue, { PropType } from "vue"
import DateTimePicker from "../DateTimePicker.vue"
import { format, addWeeks, isAfter } from "date-fns"

export default Vue.extend({
  name: "UpdateAvailabilityDialog",
  components: {
    DateTimePicker,
  },
  props: {
    value: { // used to make v-model works
      type: Boolean,
      default: false
    },
    selectedSW: { // super_worker selection from Parangon
      type: Array as PropType<number[]>,
      required: true,
    },
    workersData: { // worker data passed from parent for formatting messages
      type: Array as PropType<{id: string | number, last_name: string, first_name: string}[]>,
      default: () => []
    },
    // Optional props to pre-fill dialog
    initialStatus: {
      type: String,
      default: ""
    },
    initialStartDate: {
      type: Object,
      default: null
    },
    initialEndDate: {
      type: Object,
      default: null
    },
    initialComment: {
      type: String,
      default: ""
    },
    initialAvailabilityId: {
      type: Number,
      default: null
    },
    edit: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      valid: true,
      loading: false,
      status: "",
      previousStatus: "",
      showStatusError: false,
      startDate: {dateObject: null as Date | null, dateString: "", timeString: ""},
      endDate: {dateObject: null as Date | null, dateString: "", timeString: ""},
      followUpDate: {dateObject: null as Date | null, dateString: "", timeString: ""},
      addFollowUp: true,
      comment: "",
      rules: {
        required: (v: string) => !!v || this.$vuetify.lang.t("$vuetify.required_field"),
      },
      selectedPositions: [] as number[]
    }
  },
  mounted() {
    // Initialize when component is mounted if it's already visible
    if (this.value) {
      this.init();
    }
  },
  watch: {
    inputValue: function (val: boolean) {
      if (val === true) { // reset the component when it is shown
        this.init()
      }
    },
    status(newValue) {
      // Hide error when status is set
      if (newValue) {
        this.showStatusError = false;
      }

      // Always ensure addFollowUp is false when status is contract
      if (newValue === 'contract') {
        this.addFollowUp = false;
      } else if (this.previousStatus === 'contract') {
        // When changing from contract to another status, re-enable follow-up
        this.addFollowUp = true;
      }

      // Store current status for future reference
      this.previousStatus = newValue;

      // Validate form
      this.validateForm();
    },
    addFollowUp() {
      // Re-validate form when follow-up toggle changes
      this.validateForm();
    },
    startDate: {
      deep: true,
      handler(newVal) {
        // Ensure dateString and timeString are set properly
        if (newVal && newVal.dateObject && (!newVal.dateString || !newVal.timeString)) {
          const date = new Date(newVal.dateObject);
          this.startDate = {
            dateObject: date,
            dateString: format(date, 'yyyy-MM-dd'),
            timeString: format(date, 'HH:mm:ss')
          };
        }

        this.validateForm();
      }
    },
    endDate: {
      deep: true,
      handler(newVal) {
        // Ensure dateString and timeString are set properly
        if (newVal && newVal.dateObject && (!newVal.dateString || !newVal.timeString)) {
          const date = new Date(newVal.dateObject);
          this.endDate = {
            dateObject: date,
            dateString: format(date, 'yyyy-MM-dd'),
            timeString: format(date, 'HH:mm:ss')
          };
        }

        this.validateForm();
      }
    },
    followUpDate: {
      deep: true,
      handler() {
        this.validateForm();
      }
    }
  },
  computed: {
    inputValue: { // bind value prop to our v-model on v-dialog
      get(): boolean {
        return this.value
      },
      set(val: boolean) {
        this.$emit('input', val)
      }
    },
    formIsValid() {
      // Basic validation checks
      // Status is required
      if (!this.status) {
        return false;
      }

      // Start and end dates are required and must be valid
      if (!this.startDate.dateObject || !this.endDate.dateObject) {
        return false;
      }

      // Check if the end date is after the start date
      if (isAfter(this.startDate.dateObject, this.endDate.dateObject)) {
        return false;
      }

      // If follow-up is enabled and we're not in contract status, validate follow-up date
      if (this.addFollowUp && this.status !== 'contract' && !this.followUpDate.dateObject) {
        return false;
      }

      return true;
    }
  },
  methods: {
    /**
     * Validates the form and updates valid state
     */
    validateForm() {
      // Update valid property based on formIsValid
      this.valid = this.formIsValid;
    },

    /**
     * Resets the component's state
     */
    init() {
      // display snackbar error and hide component if no super_worker in selection
      if (this.selectedSW.length === 0) {
        EventBus.$emit('snackbar', { message: this.$vuetify.lang.t('$vuetify.empty_selection'), color: 'error' })
        this.inputValue = false
        return
      }

      // Reset form values
      this.valid = true
      this.status = this.initialStatus || "available" // Default to available status
      this.previousStatus = "" // Reset previous status
      this.showStatusError = false
      this.comment = this.initialComment || ""
      this.addFollowUp = this.status !== 'contract' // Set addFollowUp based on initial status

      // Set default dates
      const now = new Date()
      const nextWeek = addWeeks(now, 1)

      // Set start date using initialStartDate or default to now
      if (this.initialStartDate && typeof this.initialStartDate === 'object') {
        this.startDate = this.initialStartDate
      } else {
        this.startDate = {
          dateObject: now,
          dateString: format(now, 'yyyy-MM-dd'),
          timeString: format(now, 'HH:mm:ss')
        }
      }

      // Set end date using initialEndDate or default to now + 1 week
      if (this.initialEndDate && typeof this.initialEndDate === 'object') {
        this.endDate = this.initialEndDate
      } else {
        this.endDate = {
          dateObject: nextWeek,
          dateString: format(nextWeek, 'yyyy-MM-dd'),
          timeString: format(now, 'HH:mm:ss')
        }
      }

      // Set follow up date to now + 1 week
      this.followUpDate = {
        dateObject: nextWeek,
        dateString: format(nextWeek, 'yyyy-MM-dd'),
        timeString: format(nextWeek, 'HH:mm')
      }

      // Validate form with initial values
      this.validateForm();
    },

    /**
     * Find worker data by ID
     */
    findWorkerData(workerId: string | number) {
      // Convert workerId to string for comparison if it's a number
      const idToFind = workerId.toString();

      // Find the worker with matching ID in the workersData
      return this.workersData.find(worker => worker.id.toString() === idToFind);
    },

    /**
     * Submit the form and register the availability
     */
    submit() {
      // Show error if status is not selected
      if (!this.status) {
        this.showStatusError = true;
        return;
      }

      // Check validation
      if (!this.formIsValid) {
        return;
      }

      this.loading = true;

      // Ensure time strings are properly formatted
      if (this.startDate.timeString.length < 8) {
        this.startDate.timeString = this.startDate.timeString + ':00'
      }

      if (this.endDate.timeString.length < 8) {
        this.endDate.timeString = this.endDate.timeString + ':00'
      }

      // Create request body
      const body: {
        id: number[],
        add_availability: {
          status: string,
          date_begin: string,
          date_end: string,
          comment?: string,
          date_follow_up?: string,
          author: string,
        }
      } = {
        id: this.selectedSW,
        add_availability: {
          status: this.status,
          date_begin: this.formatDateTime(this.startDate),
          date_end: this.formatDateTime(this.endDate),
          author: "agency"
        }
      }

      // Add comment if provided
      if (this.comment.trim()) {
        body.add_availability.comment = this.comment
      }

      // Add follow up date if enabled and not in contract status
      if (this.addFollowUp && this.status !== 'contract') {
        body.add_availability.date_follow_up = this.formatDateTime(this.followUpDate);
      }

      // Send the request
      axios.post('/v1/workers/action', body)
        .then((response: AxiosResponse) => {
          // Close the dialog
          this.inputValue = false;

          // Emit success event
          this.$emit('success', true);

          // Check if there are any worker-specific messages in the response
          if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
            const entries = Object.entries(response.data);

            if (entries.length > 0) {
              // Convert to a structured message format with worker names
              const workerMessages = entries.map(([workerId, message]) => {
                const translatedMessage = message ? this.$vuetify.lang.t(`$vuetify.${message}`) : '';

                // Find worker data for this ID
                const workerData = this.findWorkerData(workerId);

                if (workerData) {
                  // Format with worker name and first name
                  return `${workerData.last_name.toUpperCase()} ${workerData.first_name}: ${translatedMessage}`;
                } else {
                  // Fallback to just the message
                  return translatedMessage;
                }
              });

              // Use the new structured message format
              EventBus.$emit("snackbar", {
                color: 'warning',
                timeout: Math.min(5000 + (entries.length * 1000), 10000), // 5 seconds + 1 second per worker
                structuredMessage: {
                  title: this.$vuetify.lang.t("$vuetify.workers_in_error"),
                  items: workerMessages
                }
              });
            } else {
              // No specific messages, show general success message
              EventBus.$emit("snackbar", {
                message: this.$vuetify.lang.t("$vuetify.success_register_availability")
              });
            }
          } else {
            // Standard success message
            EventBus.$emit("snackbar", {
              message: this.$vuetify.lang.t("$vuetify.success_register_availability")
            });
          }
        })
        .catch((error: AxiosError) => {
          EventBus.$emit("snackbar", { axiosError: error });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deleteAvailability() {
      if (this.edit) {
        axios.delete(`/v1/agenda/${this.selectedSW[0]}/availabilities/${this.initialAvailabilityId}`)
          .then(() => {
            this.inputValue = false;
            // Emit success event
            this.$emit('success', true);
            EventBus.$emit("snackbar", { message: this.$vuetify.lang.t("$vuetify.delete_availability_success") });
          })
          .catch((error: AxiosError) => {
            EventBus.$emit("snackbar", { axiosError: error });
          })
      }
    },
    updateAvailability() {
      if (this.edit) {
        // Check validation
        if (!this.formIsValid) {
          return;
        }

        this.loading = true;
        const body: {
          status: string,
          date_begin: string,
          date_end: string,
          date_action?: string | null,
          comment?: string,
          date_follow_up?: string,
          author: string,
          overwrite?: boolean
        } = {
          status: this.status,
          date_begin: this.formatDateTime(this.startDate),
          date_end: this.formatDateTime(this.endDate),
          date_action: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
          author: 'agency',
          overwrite: true
        }

        // Add comment if provided
        if (this.comment.trim()) {
          body.comment = this.comment
        }

        // Add follow up date if enabled and not in contract status
        if (this.addFollowUp && this.status !== 'contract') {
          body.date_follow_up = this.formatDateTime(this.followUpDate)
        }
        axios.put(`/v1/agenda/${this.selectedSW[0]}/availabilities/${this.initialAvailabilityId}`, body)
          .then(() => {
            this.inputValue = false;
            // Emit success event
            this.$emit('success', true);
            EventBus.$emit("snackbar", { message: this.$vuetify.lang.t("$vuetify.update_availability_success")});
          })
          .catch((error: AxiosError) => {
            EventBus.$emit("snackbar", { axiosError: error });
          })
          .finally(() => {
            this.loading = false;
          })
      }
    },

    /**
     * Formats a datetime object into a proper datetime string
     * Always formats date as YYYY-MM-DD and time as HH:mm:00
     */
    formatDateTime(dateObj: {dateString: string, timeString: string, dateObject?: Date | null}): string {
      // If dateObject exists, use it to generate properly formatted date and time
      if (dateObj.dateObject) {
        const date = new Date(dateObj.dateObject);

        // Format date as YYYY-MM-DD
        const formattedDate = format(date, 'yyyy-MM-dd');

        // Format time as HH:mm:00 (always use 00 for seconds)
        const formattedTime = format(date, 'HH:mm') + ':00';

        return `${formattedDate} ${formattedTime}`;
      }

      // Fallback to existing strings if dateObject is missing
      let dateString = dateObj.dateString;
      let timeString = dateObj.timeString;

      // Ensure timeString has seconds (HH:MM:SS)
      if (timeString) {
        if (timeString.length < 8) {
          timeString += ':00';
        }
      }

      return `${dateString} ${timeString}`;
    }
  }
})
